import React from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Clock } from "lucide-react";
import { PageShell, Eyebrow } from "../components/Layout";
import { INSIGHTS } from "../data/mock";

export const Insights = () => (
  <PageShell>
    <section className="hero-grid border-b border-neutral-200">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8 pt-16 pb-14">
        <Eyebrow>Risk advisory · weekly</Eyebrow>
        <h1 className="font-heading text-5xl lg:text-[60px] font-bold tracking-tight text-ink leading-[1.02]">Insights &amp; Briefings</h1>
        <p className="mt-6 text-[16px] text-ink/60 max-w-2xl leading-relaxed">Practitioner-grade analysis of Australian regulatory change — what it says, what supervisors will probe, and what to do by Friday.</p>
      </div>
    </section>
    <section className="py-16">
      <div className="max-w-[1320px] mx-auto px-5 lg:px-8">
        {/* Featured */}
        <Link to={`/insights/${INSIGHTS[0].slug}`} data-testid="insight-featured" className="group grid grid-cols-1 lg:grid-cols-12 gap-8 border border-neutral-200 rounded-lg p-8 lg:p-10 mb-8 hover:border-accent/40 hover:shadow-[0_20px_50px_rgba(15,27,45,0.08)] transition-[border-color,box-shadow] duration-200 bg-white">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-4 mb-4">
              <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-white bg-accent px-2 py-1 rounded-sm">Featured</span>
              <span className="font-mono text-[11px] text-neutral-400">{INSIGHTS[0].cat} · {INSIGHTS[0].date}</span>
            </div>
            <h2 className="font-heading text-3xl lg:text-4xl font-bold text-ink leading-[1.1] group-hover:text-accent transition-colors duration-200">{INSIGHTS[0].title}</h2>
            <p className="text-[14.5px] text-ink/60 leading-relaxed mt-4 max-w-2xl">{INSIGHTS[0].excerpt}</p>
          </div>
          <div className="lg:col-span-4 flex lg:flex-col items-start lg:items-end justify-between lg:justify-center gap-4">
            <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-neutral-400"><Clock size={12} /> {INSIGHTS[0].read} read</span>
            <span className="inline-flex items-center gap-1.5 text-accent text-[13.5px] font-semibold group-hover:gap-2.5 transition-[gap] duration-200">Read analysis <ArrowRight size={14} /></span>
          </div>
        </Link>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {INSIGHTS.slice(1).map((a) => (
            <Link key={a.slug} to={`/insights/${a.slug}`} data-testid={`insight-card-${a.slug}`}
              className="group border border-neutral-200 rounded-lg p-6 bg-white hover:border-accent/40 hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(15,27,45,0.08)] transition-[border-color,transform,box-shadow] duration-200 flex flex-col">
              <div className="flex items-center justify-between mb-3">
                <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{a.cat}</span>
                <span className="font-mono text-[10.5px] text-neutral-400">{a.date}</span>
              </div>
              <h3 className="font-heading text-[19px] font-bold text-ink leading-snug group-hover:text-accent transition-colors duration-200">{a.title}</h3>
              <p className="text-[13px] text-ink/55 leading-relaxed mt-3 line-clamp-3 flex-1">{a.excerpt}</p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-neutral-100">
                <span className="inline-flex items-center gap-1.5 font-mono text-[10.5px] text-neutral-400"><Clock size={11} /> {a.read}</span>
                <span className="inline-flex items-center gap-1 text-accent text-[12.5px] font-semibold">Read <ArrowRight size={13} /></span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  </PageShell>
);

export const InsightDetail = () => {
  const { slug } = useParams();
  const a = INSIGHTS.find((x) => x.slug === slug) || INSIGHTS[0];
  const more = INSIGHTS.filter((x) => x.slug !== a.slug).slice(0, 2);

  return (
    <PageShell>
      <section className="hero-grid border-b border-neutral-200">
        <div className="max-w-[860px] mx-auto px-5 lg:px-8 pt-14 pb-12">
          <Link to="/insights" className="inline-flex items-center gap-1.5 font-mono text-[11.5px] text-neutral-400 hover:text-accent transition-colors duration-150 mb-8" data-testid="back-to-insights">
            <ArrowLeft size={13} /> ALL INSIGHTS
          </Link>
          <div className="flex items-center gap-4 mb-4">
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-accent">{a.cat}</span>
            <span className="font-mono text-[11px] text-neutral-400">{a.date} · {a.read} read</span>
          </div>
          <h1 className="font-heading text-4xl lg:text-[46px] font-bold tracking-tight text-ink leading-[1.08]">{a.title}</h1>
        </div>
      </section>
      <article className="py-14">
        <div className="max-w-[860px] mx-auto px-5 lg:px-8">
          <p className="text-[17px] text-ink/75 leading-[1.85] font-medium border-l-[3px] border-accent pl-5 mb-10">{a.excerpt}</p>
          <div className="space-y-6">
            {a.body.map((p, i) => (
              <p key={i} className="text-[15.5px] text-ink/70 leading-[1.9]">{p}</p>
            ))}
          </div>
          <div className="mt-12 bg-paper border border-neutral-200 rounded-lg p-6">
            <p className="font-mono text-[10.5px] tracking-[0.25em] uppercase text-neutral-400 mb-2">Disclaimer</p>
            <p className="text-[12.5px] text-ink/50 leading-relaxed">General information only — not legal, financial or professional advice. Verify obligations against the current instruments and your entity's circumstances.</p>
          </div>
          {more.length > 0 && (
            <div className="mt-14">
              <h2 className="font-heading text-2xl font-bold text-ink mb-6">Keep reading</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {more.map((m) => (
                  <Link key={m.slug} to={`/insights/${m.slug}`} className="border border-neutral-200 rounded-lg p-5 hover:border-accent/40 transition-colors duration-200">
                    <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-accent">{m.cat}</span>
                    <h4 className="font-heading font-bold text-ink mt-2 leading-snug">{m.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </article>
    </PageShell>
  );
};
